<script setup lang="ts">
import { computed } from 'vue';
import { useRoute } from 'vue-router';

import { AlgoliaVariant } from '@/api/algolia';
import ProductCardWithOptions, {
  Layout,
} from '@/components/product-card/ProductCardWithOptions.vue';
import { useFeatureFlags } from '@/composables/useFeatureFlags';
import { ListSourceAttribution } from '@/utils/analytics/listSourceAttribution';
import { ProductCard as ProductCardModel, ProductCardData } from '@/utils/productCard';

interface WebstoreTag {
  facetGroup: string;
  hidden: boolean;
  showOnSearchPage: boolean;
  tagName: string;
}
interface Props {
  departmentHierarchy?: string[];
  item: AlgoliaVariant;
  layout?: Layout;
  page: string;
  query?: string;
  tags?: WebstoreTag[];
  title?: string;
  trackingEventLocation: string;
}
const props = withDefaults(defineProps<Props>(), {
  layout: 'plp',
  departmentHierarchy: () => [],
  tags: () => [],
});

const route = useRoute();
const { flags } = useFeatureFlags();

const breadCrumb = computed(() =>
  props.departmentHierarchy.length > 0
    ? props.departmentHierarchy.join(' > ')
    : props.tags.map((t) => t.tagName).join(' > '),
);

const pageBreadCrumb = computed(() => [props.page, breadCrumb.value].filter((s) => !!s).join(': '));

const productCardData = computed<ProductCardData>(() => ProductCardModel.fromAlgolia(props.item));

const applySubstitutions = computed(
  () => !!flags.ppaDefaultToSmallestVariant && !!productCardData.value.substituteVariant,
);

const requestUrl = computed(() => route.fullPath);

const indexName = computed<string>(
  () =>
    (Array.isArray(route.query.sortBy) ? route.query.sortBy[0] : route.query.sortBy) ?? 'Products',
);

const listSourceAttribution = computed<ListSourceAttribution>(() => ({
  listAlgoliaSearchId: productCardData.value.searchQueryId,
  listSource: 'Algolia',
  listTitle: props.title,
  query: props.query,
}));

// eslint-disable-next-line no-underscore-dangle
const resultPosition = computed(() => props.item.__position);
</script>

<script lang="ts">
// eslint-disable-next-line import/first
import { BuilderComponent } from '@/utils/cms';

export const ResultCardRegistration: BuilderComponent = {
  name: 'Result Card',
  inputs: [
    {
      name: 'item',
      type: 'object',
      helperText: 'Algolia search hit',
    },
    {
      name: 'page',
      type: 'string',
    },
    {
      name: 'trackingEventLocation',
      helperText: 'Page where the result card is displayed',
      type: 'string',
      advanced: true,
    },
  ],
};
</script>

<template>
  <ProductCardWithOptions
    :analyticsList="pageBreadCrumb"
    :applySubstitutions
    compareSkus
    enableSocialProof
    impressionTrackable
    :indexName="indexName"
    :layout="layout"
    :listSourceAttribution
    pageSection="Product List"
    :position="resultPosition"
    :product="productCardData"
    :requestUrl="requestUrl"
    showBundleDiscountBadge
    :trackingEventLocation="trackingEventLocation"
  />
</template>
